/**
 * Auth store (Zustand) — EPHEMERAL client/UI state only (ENGINEERING_STANDARDS): the in-memory
 * session, current user, current lab, and the effective capability Set. NO server-derived domain
 * data here (that lives in TanStack Query). NOTHING security-sensitive is persisted to
 * localStorage — the access token lives in the framework-free tokenStore closure; this store keeps
 * only the decoded identity + the capability Set for synchronous can() checks.
 *
 * The capability Set is fetched LIVE from the server (the JWT carries no caps — F-02) and re-fetched
 * on any membership/grant change signal via refreshCapabilities().
 */
import { create } from "zustand";
import {
  ApiError,
  loginRequest,
  logoutRequest,
  queryClient,
  tokenStore,
  type CaptchaProof,
} from "@/api";
import {
  decodeAccessClaims,
  fetchSession,
  type CurrentUser,
  type LabRef,
} from "./sessionProvider";
import { hasCapability } from "./capabilities";

export type AuthStatus = "idle" | "loading" | "authenticated" | "error";

interface AuthState {
  status: AuthStatus;
  user: CurrentUser | null;
  lab: LabRef | null;
  /** The effective capability Set — server-authoritative. */
  capabilities: ReadonlySet<string>;
  error: ApiError | null;

  /** Synchronous capability check (the can() core). */
  can: (capability: string) => boolean;
  /** True when EVERY listed capability is held (ALL semantics — route gating). */
  canAll: (capabilities: ReadonlyArray<string>) => boolean;
  /** True when AT LEAST ONE listed capability is held (ANY semantics). */
  canAny: (capabilities: ReadonlyArray<string>) => boolean;

  login: (
    input: {
      email: string;
      password: string;
      laboratoireId?: string | number;
      /** SaaS lab code — the Pharmakon-issued `Laboratoire.reference` (multi-lab login). */
      laboratoireRef?: string;
    },
    /** The captcha proof from the visible CaptchaField (prod-gated login); optional in dev/test. */
    captcha?: CaptchaProof,
  ) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-pull the effective set after a grant/deny/membership change. */
  refreshCapabilities: () => Promise<void>;
  /** Purge everything (called by tokenStore on session-end / reuse-detection). */
  hardReset: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "idle",
  user: null,
  lab: null,
  capabilities: new Set<string>(),
  error: null,

  can: (capability) => hasCapability(get().capabilities, capability),
  canAll: (caps) => caps.every((c) => hasCapability(get().capabilities, c)),
  canAny: (caps) => caps.length === 0 || caps.some((c) => hasCapability(get().capabilities, c)),

  login: async ({ email, password, laboratoireId, laboratoireRef }, captcha) => {
    set({ status: "loading", error: null });
    try {
      const pair = await loginRequest(
        {
          email,
          password,
          ...(laboratoireId !== undefined ? { laboratoireId } : {}),
          ...(laboratoireRef ? { laboratoireRef } : {}),
        },
        captcha,
      );
      tokenStore.setFromPair(pair);
      const claims = decodeAccessClaims(pair.access_token);
      const bundle = await fetchSession(claims ?? undefined);
      set({
        status: "authenticated",
        user: bundle.user,
        lab: bundle.lab,
        capabilities: new Set(bundle.capabilities),
        error: null,
      });
    } catch (err) {
      tokenStore.clear();
      set({
        status: "error",
        user: null,
        lab: null,
        capabilities: new Set(),
        error: err instanceof ApiError ? err : new ApiError("INTERNAL", "Échec de connexion", 500),
      });
      throw err;
    }
  },

  logout: async () => {
    try {
      await logoutRequest();
    } catch {
      // best-effort; the server revokes the refresh family regardless.
    } finally {
      tokenStore.clear();
      queryClient.clear();
      set({ status: "idle", user: null, lab: null, capabilities: new Set(), error: null });
    }
  },

  refreshCapabilities: async () => {
    if (get().status !== "authenticated") return;
    const claims = decodeAccessClaims(tokenStore.getAccessToken());
    const bundle = await fetchSession(claims ?? undefined);
    set({ user: bundle.user, lab: bundle.lab, capabilities: new Set(bundle.capabilities) });
  },

  hardReset: () => {
    queryClient.clear();
    set({ status: "idle", user: null, lab: null, capabilities: new Set(), error: null });
  },
}));

/** Wire the tokenStore session-end hook to the auth store purge (call once at boot). */
export function bindSessionEndToAuthStore(): void {
  tokenStore.registerSessionEndedHandler(() => {
    useAuthStore.getState().hardReset();
  });
}
